import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  IconButton,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { LuUnlink, LuMessageSquare, LuChevronDown, LuCheck } from 'react-icons/lu';
import { FiLinkedin } from 'react-icons/fi';
import api from '../../../services/api';
import LinkedInConversationView from './LinkedInConversationView';

// ── Helpers ───────────────────────────────────────────────────────────────────

const relativeTime = (isoString) => {
  if (!isoString) return '';
  const diffMs  = Date.now() - new Date(isoString).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffH   = Math.floor(diffMs / 3_600_000);
  const diffD   = Math.floor(diffMs / 86_400_000);
  if (diffMin < 1)  return 'just now';
  if (diffMin < 60) return `${diffMin}m`;
  if (diffH   < 24) return `${diffH}h`;
  if (diffD   <  7) return new Date(isoString).toLocaleDateString('en-US', { weekday: 'short' });
  return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const STATUS_STYLES = {
  ACTIVE:      { bg: 'rgba(34,197,94,0.12)',  color: 'rgba(34,197,94,0.85)' },
  IN_PROGRESS: { bg: 'rgba(34,197,94,0.12)',  color: 'rgba(34,197,94,0.85)' },
  PAUSED:      { bg: 'rgba(251,191,36,0.12)', color: 'rgba(251,191,36,0.85)' },
  DRAFT:       { bg: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.4)' },
};

const StatusPill = ({ status }) => {
  if (!status) return null;
  const styles = STATUS_STYLES[status.toUpperCase()] || STATUS_STYLES.DRAFT;
  return (
    <Box
      as="span"
      px="6px"
      py="2px"
      borderRadius="4px"
      fontSize="10px"
      fontWeight="500"
      lineHeight="1.4"
      letterSpacing="0.02em"
      bg={styles.bg}
      color={styles.color}
      flexShrink={0}
    >
      {status}
    </Box>
  );
};

// Fully custom dropdown — native <select> bypassed for complete styling control
const CampaignDropdown = ({ campaigns, value, onChange, isLoading, onOpen }) => {
  const [open, setOpen] = useState(false);
  const containerRef    = useRef(null);

  const selected = campaigns.find(c => String(c.id) === value);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleTriggerClick = () => {
    if (!open && onOpen) onOpen();
    setOpen(prev => !prev);
  };

  return (
    <Box position="relative" ref={containerRef}>
      {/* Trigger */}
      <HStack
        as="button"
        type="button"
        w="100%"
        px={3}
        py="9px"
        borderRadius="10px"
        border="1px solid rgba(255,255,255,0.10)"
        bg="#0d0d0d"
        cursor={isLoading ? 'not-allowed' : 'pointer'}
        onClick={!isLoading ? handleTriggerClick : undefined}
        spacing={2}
        justify="space-between"
        transition="border-color 0.15s, background 0.15s"
        _hover={{ borderColor: 'rgba(255,255,255,0.20)', bg: 'rgba(255,255,255,0.03)' }}
        outline="none"
        sx={{ WebkitAppearance: 'none' }}
      >
        {isLoading ? (
          <HStack spacing={2} flex={1}>
            <Spinner size="xs" color="rgba(255,255,255,0.3)" />
            <Text fontSize="sm" color="rgba(255,255,255,0.3)">Loading campaigns…</Text>
          </HStack>
        ) : selected ? (
          <HStack spacing={2} flex={1} overflow="hidden">
            <Text fontSize="sm" color="#fafafa" noOfLines={1} flex={1} textAlign="left">
              {selected.name}
            </Text>
            <StatusPill status={selected.status} />
          </HStack>
        ) : (
          <Text fontSize="sm" color="rgba(255,255,255,0.3)" flex={1} textAlign="left">
            Choose a campaign…
          </Text>
        )}
        <Box
          color="rgba(255,255,255,0.3)"
          transition="transform 0.15s"
          transform={open ? 'rotate(180deg)' : 'rotate(0deg)'}
          flexShrink={0}
        >
          <LuChevronDown size={14} />
        </Box>
      </HStack>

      {/* Dropdown panel */}
      {open && (
        <Box
          position="absolute"
          top="calc(100% + 6px)"
          left={0}
          right={0}
          zIndex={50}
          bg="#111111"
          border="1px solid rgba(255,255,255,0.10)"
          borderRadius="12px"
          boxShadow="0 8px 32px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4)"
          overflow="hidden"
          py={1}
        >
          {campaigns.length === 0 ? (
            <Text px={3} py="10px" fontSize="sm" color="rgba(255,255,255,0.3)">
              No campaigns found
            </Text>
          ) : (
            campaigns.map(c => {
              const isActive = String(c.id) === value;
              return (
                <HStack
                  key={c.id}
                  as="button"
                  type="button"
                  w="100%"
                  px={3}
                  py="9px"
                  spacing={2}
                  cursor="pointer"
                  bg={isActive ? 'rgba(255,255,255,0.07)' : 'transparent'}
                  _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                  onClick={() => { onChange(String(c.id)); setOpen(false); }}
                  outline="none"
                >
                  <Text fontSize="sm" color="#fafafa" flex={1} textAlign="left" noOfLines={1}>
                    {c.name}
                  </Text>
                  <StatusPill status={c.status} />
                  {isActive && (
                    <Box color="rgba(255,255,255,0.5)" flexShrink={0}>
                      <LuCheck size={13} />
                    </Box>
                  )}
                </HStack>
              );
            })
          )}
        </Box>
      )}
    </Box>
  );
};

// ── LinkedInInboxPanel ────────────────────────────────────────────────────────

const LinkedInInboxPanel = forwardRef(({ campaignId, onLoadingChange }, ref) => {
  const toast = useToast();

  const [isLoadingLink,          setIsLoadingLink]          = useState(true);
  const [linkedCampaign,         setLinkedCampaign]         = useState(null);
  const [campaigns,              setCampaigns]              = useState([]);
  const [isLoadingCampaigns,     setIsLoadingCampaigns]     = useState(false);
  const [selectedCampaignId,     setSelectedCampaignId]     = useState('');
  const [isSavingLink,           setIsSavingLink]           = useState(false);
  const [conversations,          setConversations]          = useState([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [selectedConversation,   setSelectedConversation]   = useState(null);

  // ── Check link status on mount ────────────────────────────────────────────
  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;

    (async () => {
      setIsLoadingLink(true);
      try {
        const { data } = await api.get('/api/integrations/heyreach/campaign-link', {
          params: { campaign_id: campaignId },
        });
        if (!cancelled) setLinkedCampaign(data.linked ? data : null);
      } catch {
        if (!cancelled) setLinkedCampaign(null);
      } finally {
        if (!cancelled) setIsLoadingLink(false);
      }
    })();

    return () => { cancelled = true; };
  }, [campaignId]);

  // ── Fetch conversations when linked ───────────────────────────────────────
  const fetchConversations = useCallback(async () => {
    if (!linkedCampaign || !campaignId) return;
    setIsLoadingConversations(true);
    try {
      const { data } = await api.get('/api/integrations/heyreach/conversations', {
        params: { campaign_id: campaignId },
      });
      setConversations(data.conversations || []);
    } catch {
      setConversations([]);
    } finally {
      setIsLoadingConversations(false);
    }
  }, [linkedCampaign, campaignId]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Poll for new conversations every 2 minutes while the panel is mounted
  // and a campaign is linked.  The backend only hits HeyReach when data is
  // stale (>15 min), so this is cheap the majority of the time.
  useEffect(() => {
    if (!linkedCampaign) return;
    const timer = setInterval(fetchConversations, 2 * 60 * 1000);
    return () => clearInterval(timer);
  }, [linkedCampaign, fetchConversations]);

  // Expose refresh() to parent (InboxPanel top toolbar)
  useImperativeHandle(ref, () => ({ refresh: fetchConversations }), [fetchConversations]);

  // Propagate loading state to parent so top refresh button shows spinner
  useEffect(() => {
    onLoadingChange?.(isLoadingConversations);
  }, [isLoadingConversations, onLoadingChange]);

  // ── Lazy-load HeyReach campaigns for link dropdown ────────────────────────
  const fetchCampaigns = useCallback(async () => {
    if (campaigns.length > 0 || isLoadingCampaigns) return;
    setIsLoadingCampaigns(true);
    try {
      const { data } = await api.get('/api/integrations/heyreach/campaigns');
      setCampaigns(data.campaigns || []);
    } catch {
      setCampaigns([]);
      toast({
        title:    'Could not load HeyReach campaigns',
        status:   'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingCampaigns(false);
    }
  }, [campaigns.length, isLoadingCampaigns, toast]);

  // ── Link ──────────────────────────────────────────────────────────────────
  const handleLink = async () => {
    const campaign = campaigns.find(c => String(c.id) === selectedCampaignId);
    if (!campaign || isSavingLink) return;
    setIsSavingLink(true);
    try {
      const { data } = await api.post('/api/integrations/heyreach/link-campaign', {
        campaign_id:           campaignId,
        heyreach_campaign_id:  String(campaign.id),
        heyreach_campaign_name: campaign.name,
      });
      setLinkedCampaign(data);
    } catch {
      toast({ title: 'Failed to link campaign', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setIsSavingLink(false);
    }
  };

  // ── Unlink ────────────────────────────────────────────────────────────────
  const handleUnlink = async () => {
    try {
      await api.delete('/api/integrations/heyreach/link-campaign', {
        params: { campaign_id: campaignId },
      });
      setLinkedCampaign(null);
      setConversations([]);
      setSelectedConversation(null);
    } catch {
      toast({ title: 'Failed to unlink campaign', status: 'error', duration: 3000, isClosable: true });
    }
  };

  // ── Conversation selected → detail view ───────────────────────────────────
  if (selectedConversation) {
    return (
      <LinkedInConversationView
        conversation={selectedConversation}
        onBack={() => setSelectedConversation(null)}
      />
    );
  }

  // ── Loading link status ───────────────────────────────────────────────────
  if (isLoadingLink) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" flex={1} h="120px">
        <Spinner size="sm" color="rgba(255,255,255,0.4)" />
      </Box>
    );
  }

  // ── No campaign linked ────────────────────────────────────────────────────
  if (!linkedCampaign) {
    return (
      <Box flex={1} px={4} pt={6} pb={4}>
        <VStack spacing={5} align="stretch">

          <VStack spacing={2.5} align="center" textAlign="center">
            <Box
              w="46px"
              h="46px"
              borderRadius="10px"
              bg="rgba(10,102,194,0.08)"
              border="1px solid rgba(10,102,194,0.2)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="rgba(10,102,194,0.85)"
            >
              <FiLinkedin size={20} />
            </Box>
            <Text color="#fafafa" fontSize="sm" fontWeight="600">Connect a campaign</Text>
            <Text color="rgba(255,255,255,0.35)" fontSize="xs" lineHeight="1.65" maxW="210px">
              Select the HeyReach campaign that uses this campaign's leads to track LinkedIn conversations.
            </Text>
          </VStack>

          <Box>
            <Text color="rgba(255,255,255,0.45)" fontSize="xs" fontWeight="500" mb={1.5}>
              HeyReach campaign
            </Text>
            <CampaignDropdown
              campaigns={campaigns}
              value={selectedCampaignId}
              onChange={setSelectedCampaignId}
              isLoading={isLoadingCampaigns}
              onOpen={fetchCampaigns}
            />
          </Box>

          <Button
            size="sm"
            bg="#fafafa"
            color="#0a0a0a"
            borderRadius="8px"
            fontWeight="600"
            fontSize="sm"
            isDisabled={!selectedCampaignId}
            isLoading={isSavingLink}
            onClick={handleLink}
            _hover={{ opacity: 0.85 }}
            _focus={{ boxShadow: 'none' }}
          >
            Link Campaign
          </Button>

        </VStack>
      </Box>
    );
  }

  // ── Linked: conversation list ─────────────────────────────────────────────
  return (
    <Box flex={1} display="flex" flexDirection="column" overflow="hidden" minH={0}>

      {/* Linked campaign sub-header */}
      <HStack
        px={4}
        py={2}
        borderBottom="1px solid rgba(255,255,255,0.06)"
        justify="space-between"
        flexShrink={0}
        overflow="hidden"
      >
        <HStack spacing={1.5} overflow="hidden" flex={1} minW={0}>
          <Box color="rgba(10,102,194,0.65)" flexShrink={0}>
            <FiLinkedin size={12} />
          </Box>
          <Text color="rgba(255,255,255,0.5)" fontSize="xs" fontWeight="500" noOfLines={1}>
            {linkedCampaign.heyreach_campaign_name}
          </Text>
        </HStack>

        <HStack spacing={0.5} flexShrink={0}>
          <IconButton
            icon={<LuUnlink size={12} />}
            onClick={handleUnlink}
            variant="ghost"
            size="xs"
            color="rgba(255,255,255,0.3)"
            aria-label="Unlink campaign"
            _hover={{ color: 'rgba(239,68,68,0.75)', bg: 'transparent' }}
            _focus={{ boxShadow: 'none' }}
          />
        </HStack>
      </HStack>

      {/* Conversation list */}
      <Box
        flex={1}
        overflowY="auto"
        minH={0}
        sx={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { bg: 'rgba(255,255,255,0.08)', borderRadius: '2px' },
        }}
      >
        {isLoadingConversations ? (
          <Box display="flex" alignItems="center" justifyContent="center" h="100px">
            <Spinner size="sm" color="rgba(255,255,255,0.4)" />
          </Box>
        ) : conversations.length === 0 ? (
          <Box display="flex" alignItems="center" justifyContent="center" px={6} pt={12}>
            <VStack spacing={3} align="center" maxW="220px">
              <Box
                w="48px"
                h="48px"
                borderRadius="12px"
                bg="rgba(255,255,255,0.04)"
                border="1px solid rgba(255,255,255,0.08)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="rgba(255,255,255,0.2)"
              >
                <LuMessageSquare size={22} />
              </Box>
              <VStack spacing={1} align="center">
                <Text color="rgba(255,255,255,0.5)" fontSize="sm" fontWeight="500">
                  No conversations yet
                </Text>
                <Text color="rgba(255,255,255,0.22)" fontSize="xs" textAlign="center" lineHeight="1.65">
                  Conversations will appear here once HeyReach begins sending messages.
                </Text>
              </VStack>
            </VStack>
          </Box>
        ) : (
          conversations.map(conv => {
            const hasReplied = conv.status === 'replied';
            return (
              <Box
                key={conv.heyreach_conversation_id}
                onClick={() => setSelectedConversation(conv)}
                cursor="pointer"
                px={4}
                py={3}
                borderBottom="1px solid rgba(255,255,255,0.06)"
                transition="background 0.12s ease"
                _hover={{ bg: 'rgba(255,255,255,0.03)' }}
                _active={{ bg: 'rgba(255,255,255,0.05)' }}
              >
                <HStack justify="space-between" align="center" mb={0.5}>
                  <Text
                    color={hasReplied ? '#fafafa' : 'rgba(255,255,255,0.55)'}
                    fontSize="xs"
                    fontWeight={hasReplied ? '600' : '400'}
                    noOfLines={1}
                    flex={1}
                    minW={0}
                  >
                    {conv.lead_name || conv.lead_linkedin_url}
                  </Text>
                  <HStack spacing={1.5} flexShrink={0} ml={2}>
                    {hasReplied && (
                      <Box
                        w="6px"
                        h="6px"
                        borderRadius="full"
                        bg="rgba(74,222,128,0.9)"
                        flexShrink={0}
                      />
                    )}
                    <Text color="rgba(255,255,255,0.25)" fontSize="10px">
                      {relativeTime(conv.last_message_at)}
                    </Text>
                  </HStack>
                </HStack>
                <Text
                  color={hasReplied ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.2)'}
                  fontSize="10px"
                  noOfLines={1}
                >
                  {conv.last_message_preview ||
                    (conv.status === 'no_reply' ? 'No reply yet' : 'Awaiting reply…')}
                </Text>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
});

LinkedInInboxPanel.displayName = 'LinkedInInboxPanel';

export default LinkedInInboxPanel;
